import React, { useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import TopbarAction from '@/components/TopbarAction'
import { useAppStore } from '@/stores/useAppStore'
import { useAlertNotifications } from './use-alert-notifications'

/** Topbar button; the warnings themselves open in the docked side panel. */
const AlertNotification: React.FC = () => {
    const { t } = useTranslation()
    const reportedFailureRef = useRef<string | null>(null)
    const sidePanel = useAppStore((state) => state.sidePanel)
    const toggleSidePanel = useAppStore((state) => state.toggleSidePanel)
    const {
        allowedScopes,
        isError,
        error,
        failedSources,
        totalCount,
        highPriorityCount,
    } = useAlertNotifications()

    useEffect(() => {
        const signature = isError
            ? `all:${error instanceof Error ? error.message : String(error)}`
            : failedSources.length > 0
              ? `partial:${failedSources.join(',')}`
              : null

        if (!signature) {
            reportedFailureRef.current = null
            return
        }
        if (reportedFailureRef.current === signature) return

        reportedFailureRef.current = signature
        if (isError) {
            Sentry.captureException(error, {
                extra: { warningFailureSignature: signature },
            })
        } else {
            Sentry.captureMessage(
                'Alert notification sources partially failed',
                {
                    level: 'warning',
                    extra: { failedSources },
                }
            )
        }
    }, [error, failedSources, isError])

    // Show component only if user has allowed scopes
    if (allowedScopes.length === 0) {
        return null
    }

    const hasHigh = highPriorityCount > 0
    const loadIssueLabel = t(
        isError
            ? 'customer:calendar_events.alert_load_error'
            : 'customer:calendar_events.alert_partial'
    )

    const label = t('alert-notification')

    const hasLoadIssue = isError || failedSources.length > 0

    return (
        <TopbarAction
            icon={AlertTriangle}
            aria-label={label}
            aria-pressed={sidePanel === 'alerts'}
            onClick={() => toggleSidePanel('alerts')}
            // Glyph follows the badge: danger with a high-priority alert, warning
            // with any other, muted at zero.
            tone={hasHigh ? 'danger' : totalCount > 0 ? 'warning' : 'default'}
            badge={{
                count: totalCount,
                tone: hasHigh ? 'danger' : 'warning',
            }}
            dot={
                hasLoadIssue
                    ? {
                          tone: isError ? 'danger' : 'warning',
                          label: loadIssueLabel,
                      }
                    : undefined
            }
        />
    )
}

export default AlertNotification
